/**
 * Client-side geometry helpers for FPS Maps shape labels.
 *
 * These mirror the small subset of the server's geoGeometry.js math that the UI
 * needs for immediate (no round-trip) display right after a shape is drawn:
 *   - polygonAreaSqMi(): "how big is this drawn/rectangle shape" in square miles,
 *     for the group-summary line and on-canvas label. Same spherical-excess
 *     ("shoelace on a sphere") formula as the server's ringAreaSqMi/polygonAreaSqMi
 *     so the numbers agree; NOT survey-grade (no datum/ellipsoid correction),
 *     which is fine for a rough size readout.
 *   - polygonCentroid(): the [lat, lon] anchor point for a drawn/rectangle shape's
 *     on-canvas label — the average of the outer ring's vertices (a simple vertex
 *     centroid, adequate for label placement on the mostly-convex shapes the draw
 *     tools produce).
 *
 * Hand-rolled and dependency-free, matching the project decision to avoid adding
 * @turf/turf (a Tier 2 dependency) when a few lines of math suffice. GeoJSON
 * coordinate order is [longitude, latitude]; Leaflet uses [latitude, longitude],
 * so polygonCentroid returns [lat, lon] ready for Leaflet APIs.
 */

const EARTH_RADIUS_MI = 3958.7613; // mean Earth radius in miles (matches server)

/**
 * Approximate the area of one linear ring (array of [lon, lat] positions) in
 * square miles via the spherical-excess formula. Ring winding sets the sign; we
 * return the absolute value. Identical approach to the server's ringAreaSqMi.
 *
 * @param {Array<[number, number]>} ring - closed ring of [lon, lat] positions
 * @returns {number} area in square miles (>= 0)
 */
function ringAreaSqMi(ring) {
  if (!Array.isArray(ring) || ring.length < 4) return 0;
  const R = EARTH_RADIUS_MI;
  const toRad = Math.PI / 180;
  let total = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const [lon1, lat1] = ring[i];
    const [lon2, lat2] = ring[i + 1];
    total += (lon2 - lon1) * toRad * (2 + Math.sin(lat1 * toRad) + Math.sin(lat2 * toRad));
  }
  return Math.abs((total * R * R) / 2);
}

/**
 * Approximate the area of a GeoJSON Polygon/MultiPolygon in square miles. The
 * first ring of a Polygon is the outer boundary; further rings are holes
 * (subtracted). A MultiPolygon sums each polygon's net area. Returns 0 for
 * anything else (radius areas don't carry geometry — their size is params.radius_mi).
 *
 * @param {object} geom - GeoJSON Polygon or MultiPolygon geometry
 * @returns {number} area in square miles (>= 0)
 */
export function polygonAreaSqMi(geom) {
  if (!geom || typeof geom !== 'object') return 0;
  const ringSetArea = (rings) => {
    if (!Array.isArray(rings) || rings.length === 0) return 0;
    const outer = ringAreaSqMi(rings[0]);
    let holes = 0;
    for (let i = 1; i < rings.length; i++) holes += ringAreaSqMi(rings[i]);
    return Math.max(0, outer - holes);
  };
  if (geom.type === 'Polygon') return ringSetArea(geom.coordinates);
  if (geom.type === 'MultiPolygon') {
    if (!Array.isArray(geom.coordinates)) return 0;
    return geom.coordinates.reduce((sum, poly) => sum + ringSetArea(poly), 0);
  }
  return 0;
}

/**
 * Vertex centroid of a GeoJSON Polygon/MultiPolygon's outer ring(s), returned as
 * [lat, lon] for Leaflet. Averages the outer-ring vertices (excluding the ring's
 * duplicated closing vertex). Good enough to anchor a label near the middle of the
 * mostly-convex shapes the draw tools produce; not a true area-weighted centroid.
 * Returns null when there's no usable geometry.
 *
 * @param {object} geom - GeoJSON Polygon or MultiPolygon geometry
 * @returns {[number, number]|null} [lat, lon], or null
 */
export function polygonCentroid(geom) {
  if (!geom || typeof geom !== 'object') return null;

  // Collect the outer ring(s): Polygon has one; MultiPolygon has one per polygon.
  let outerRings = [];
  if (geom.type === 'Polygon' && Array.isArray(geom.coordinates)) {
    if (geom.coordinates[0]) outerRings = [geom.coordinates[0]];
  } else if (geom.type === 'MultiPolygon' && Array.isArray(geom.coordinates)) {
    outerRings = geom.coordinates.map((poly) => poly && poly[0]).filter(Boolean);
  } else {
    return null;
  }

  let sumLat = 0;
  let sumLon = 0;
  let count = 0;
  for (const ring of outerRings) {
    if (!Array.isArray(ring) || ring.length < 4) continue;
    // Skip the last position: a closed ring repeats its first vertex, which would
    // otherwise double-weight that point.
    for (let i = 0; i < ring.length - 1; i++) {
      const pos = ring[i];
      if (Array.isArray(pos) && Number.isFinite(pos[0]) && Number.isFinite(pos[1])) {
        sumLon += pos[0];
        sumLat += pos[1];
        count += 1;
      }
    }
  }
  if (count === 0) return null;
  return [sumLat / count, sumLon / count];
}

/**
 * Format a square-mileage for display, e.g. 12.4 → "12.4 sq mi". One decimal
 * matches the mock-up ("12.4 sq mi"); values under 0.05 round to "0.1 sq mi" so a
 * tiny shape never reads as "0 sq mi".
 *
 * @param {number} sqMi
 * @returns {string}
 */
export function formatSqMi(sqMi) {
  if (!Number.isFinite(sqMi) || sqMi < 0) return '';
  const rounded = Math.max(0.1, Math.round(sqMi * 10) / 10);
  return `${rounded} sq mi`;
}

/**
 * Format a radius-in-miles for display, e.g. 10 → "10 mi radius", 5.5 → "5.5 mi
 * radius". Trailing-zero-free via Number's own string coercion.
 *
 * @param {number} radiusMi
 * @returns {string}
 */
export function formatRadiusMi(radiusMi) {
  if (!Number.isFinite(radiusMi) || radiusMi <= 0) return '';
  return `${radiusMi} mi radius`;
}

/**
 * Build a GeoJSON Polygon approximating a circle — mirrors the server's
 * geoGeometry.circlePolygon exactly (same algorithm, same default step count),
 * so a client-computed circle and the server's own radius-area rendering
 * agree. Used to give Porter a real polygon for "the area" when the user has
 * a radius circle selected, since a circle isn't a native GeoJSON type.
 *
 * @param {number} lat
 * @param {number} lon
 * @param {number} radiusMi
 * @param {number} [steps=64]
 * @returns {{type: 'Polygon', coordinates: number[][][]}}
 */
export function circlePolygon(lat, lon, radiusMi, steps = 64) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const toDeg = (rad) => (rad * 180) / Math.PI;
  const latRad = toRad(lat);
  const lonRad = toRad(lon);
  const angularDist = radiusMi / EARTH_RADIUS_MI;

  const ring = [];
  for (let i = 0; i <= steps; i++) {
    const bearing = toRad((i * 360) / steps);
    const destLat = Math.asin(
      Math.sin(latRad) * Math.cos(angularDist) +
        Math.cos(latRad) * Math.sin(angularDist) * Math.cos(bearing)
    );
    const destLon =
      lonRad +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angularDist) * Math.cos(latRad),
        Math.cos(angularDist) - Math.sin(latRad) * Math.sin(destLat)
      );
    ring.push([toDeg(destLon), toDeg(destLat)]);
  }
  // Ensure the ring is explicitly closed (first === last position).
  ring[ring.length - 1] = ring[0].slice();
  return { type: 'Polygon', coordinates: [ring] };
}

/**
 * Full GeoJSON polygon for any area — for contexts (Porter's "the drawn
 * area") that need an actual shape, not just an anchor point. Radius areas
 * are converted via circlePolygon; drawn/census-backed areas already carry a
 * polygon. Returns null if there's nothing usable yet (e.g. a just-added
 * census area before the next autosave round-trip resolves its geometry).
 *
 * @param {object} area
 * @returns {{type: string, coordinates: any}|null}
 */
export function areaToPolygon(area) {
  if (area.area_type === 'radius' && area.params?.center && area.params?.radius_mi) {
    const [lat, lon] = area.params.center;
    return circlePolygon(lat, lon, area.params.radius_mi);
  }
  if (area.geometry && area.geometry.type) return area.geometry;
  return null;
}
